/** Context provider for the user subscription tier (Firestore `users/{uid}.tier`). */
// @ts-check
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/features/auth/useAuth';
import {
  DEFAULT_TIER,
  subscribeToUserProfile,
} from '@/services/userService';
import { TierContext } from '@/features/tier/TierContext.def';

/**
 * @param {Object} props
 * @param {React.ReactNode} props.children
 */
export function TierProvider({ children }) {
  const { user } = useAuth();
  const [tier, setTier] = useState(/** @type {import('@/services/userService').UserTier} */ (DEFAULT_TIER));
  const [seenHints, setSeenHints] = useState(/** @type {string[]} */ ([]));
  const [loginCount, setLoginCount] = useState(0);
  const [createdAt, setCreatedAt] = useState(/** @type {Date | null} */ (null));
  const [profileDisplayName, setProfileDisplayName] = useState('');
  const [marketingOptInGranted, setMarketingOptInGranted] = useState(false);
  const [loading, setLoading] = useState(Boolean(user?.uid));
  const [profileLoaded, setProfileLoaded] = useState(!user?.uid);

  useEffect(() => {
    if (!user?.uid) {
      setTier(DEFAULT_TIER);
      setSeenHints([]);
      setLoginCount(0);
      setCreatedAt(null);
      setProfileDisplayName('');
      setMarketingOptInGranted(false);
      setLoading(false);
      setProfileLoaded(true);
      return;
    }

    setLoading(true);
    setProfileLoaded(false);

    const unsubscribe = subscribeToUserProfile(
      user.uid,
      (profile) => {
        setTier(profile.tier);
        setSeenHints(profile.seenHints);
        setLoginCount(profile.loginCount);
        setCreatedAt(profile.createdAt);
        setProfileDisplayName(profile.displayName);
        setMarketingOptInGranted(profile.marketingOptInGranted);
        setLoading(false);
        setProfileLoaded(true);
      },
      () => {
        setTier(DEFAULT_TIER);
        setSeenHints([]);
        setLoginCount(0);
        setCreatedAt(null);
        setProfileDisplayName('');
        setMarketingOptInGranted(false);
        setLoading(false);
        setProfileLoaded(true);
      },
    );

    return unsubscribe;
  }, [user?.uid]);

  const value = useMemo(
    () => ({
      tier,
      isFree: tier === 'free',
      isLite: tier === 'lite',
      isPro: tier === 'pro',
      loading,
      profileLoaded,
      seenHints,
      loginCount,
      createdAt,
      profileDisplayName,
      marketingOptInGranted,
    }),
    [
      tier,
      loading,
      profileLoaded,
      seenHints,
      loginCount,
      createdAt,
      profileDisplayName,
      marketingOptInGranted,
    ],
  );

  return (
    <TierContext.Provider value={value}>{children}</TierContext.Provider>
  );
}
